/**
 * Locked Sweet Listeners talk-session pricing and length.
 * Checkout must charge from sessionFormat, never a flat listing price.
 */

export const TALK_SESSION_LISTING_TYPE = 'talksession';
export const SESSION_LENGTH_MINUTES = 15;
export const SESSION_CURRENCY = 'USD';
export const SESSION_FORMATS = ['chat', 'voice', 'video'];

export const SESSION_FORMAT_PRICES_SUBUNITS = {
  chat: 1000,
  voice: 2000,
  video: 3000,
};

export const EXTEND_COMMISSION_RATE = 0.3;

export const extendPriceForFormat = format => {
  const amountSubunits = SESSION_FORMAT_PRICES_SUBUNITS[format] || SESSION_FORMAT_PRICES_SUBUNITS.chat;
  const applicationFeeSubunits = Math.round(amountSubunits * EXTEND_COMMISSION_RATE);
  return {
    amountSubunits,
    applicationFeeSubunits,
    listenerSubunits: amountSubunits - applicationFeeSubunits,
    priceUsd: amountSubunits / 100,
    priceLabel: String(amountSubunits / 100),
  };
};

export const EXTEND_AMOUNT_SUBUNITS = SESSION_FORMAT_PRICES_SUBUNITS.chat;
export const EXTEND_LISTENER_SUBUNITS = extendPriceForFormat('chat').listenerSubunits;
export const EXTEND_PLATFORM_SUBUNITS = extendPriceForFormat('chat').applicationFeeSubunits;
export const EXTEND_PRICE_USD = extendPriceForFormat('chat').priceUsd;
export const EXTEND_PRICE_LABEL = extendPriceForFormat('chat').priceLabel;

export const SESSION_FORMAT_VARIANT_NAMES = {
  chat: 'Text chat',
  voice: 'Voice call',
  video: 'Video call',
};

export const OFFERED_FORMATS_FIELD = 'offeredFormats';

export const isTalkSessionListing = listing => {
  return listing?.attributes?.publicData?.listingType === TALK_SESSION_LISTING_TYPE;
};

/**
 * True for a talk-session order (listing type, sessionFormat, or Start now).
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isTalkSessionTransaction = tx =>
  isTalkSessionListing(tx?.listing) ||
  SESSION_FORMATS.includes(tx?.attributes?.protectedData?.sessionFormat) ||
  tx?.attributes?.protectedData?.startNow === true;

const TALK_SESSION_CAPTURE_TRANSITIONS = [
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
  'transition/complete',
  'transition/operator-complete',
  'transition/operator-complete-separate-hold',
];

const TALK_SESSION_HOLD_RELEASE_TRANSITIONS = [
  'transition/cancel',
  'transition/customer-cancel',
  'transition/provider-cancel',
  'transition/decline',
  'transition/operator-decline',
  'transition/expire',
  'transition/expire-payment',
  'transition/expire-accepted',
  'transition/operator-cancel-separate-hold',
];

/**
 * True when a talksession PaymentIntent was captured after the session.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const talkSessionWasCaptured = tx => {
  const last = tx?.attributes?.lastTransition;
  if (TALK_SESSION_CAPTURE_TRANSITIONS.includes(last)) {
    return true;
  }
  return (tx?.attributes?.transitions || []).some(item =>
    TALK_SESSION_CAPTURE_TRANSITIONS.includes(item?.transition)
  );
};

/**
 * Talksession cancel/decline/expire before capture — a hold release, not a refund.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isTalkSessionHoldRelease = tx =>
  isTalkSessionTransaction(tx) &&
  TALK_SESSION_HOLD_RELEASE_TRANSITIONS.includes(tx?.attributes?.lastTransition) &&
  !talkSessionWasCaptured(tx);

export const talkSessionPriceVariants = () =>
  SESSION_FORMATS.map(format => ({
    name: SESSION_FORMAT_VARIANT_NAMES[format],
    priceInSubunits: SESSION_FORMAT_PRICES_SUBUNITS[format],
    bookingLengthInMinutes: SESSION_LENGTH_MINUTES,
  }));

export const sessionFormatFromVariantName = name => {
  if (!name) {
    return null;
  }
  const match = SESSION_FORMATS.find(format => SESSION_FORMAT_VARIANT_NAMES[format] === name);
  if (match) {
    return match;
  }
  const normalized = String(name)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (normalized === 'textchat' || normalized === 'chat') {
    return 'chat';
  }
  if (normalized === 'voice' || normalized === 'voicecall') {
    return 'voice';
  }
  if (normalized === 'video' || normalized === 'videocall') {
    return 'video';
  }
  return null;
};

export const resolveSessionFormat = (orderData = {}) => {
  const protectedData = orderData.protectedData || orderData.attributes?.protectedData || {};
  const raw = orderData.sessionFormat || protectedData.sessionFormat;
  if (SESSION_FORMATS.includes(raw)) {
    return raw;
  }
  return sessionFormatFromVariantName(
    orderData.priceVariantName || protectedData.priceVariantName
  );
};

/**
 * Booked talk-session format. Never guess chat when the field is missing.
 *
 * @param {Object} [tx]
 * @returns {'chat'|'voice'|'video'|null}
 */
export const sessionFormatFromTransaction = tx => resolveSessionFormat(tx || {});

export const priceInSubunitsForFormat = format => {
  return SESSION_FORMAT_PRICES_SUBUNITS[format] || null;
};

export const variantNameForFormat = format => SESSION_FORMAT_VARIANT_NAMES[format] || null;

/**
 * Booking-flow label for notices and Dashboard cards.
 * Text chat / Voice call / Video call — never a generic “session” when known.
 *
 * @param {string} [format]
 * @returns {string}
 */
export const sessionFormatDisplayName = format =>
  SESSION_FORMAT_VARIANT_NAMES[format] || 'session';

export const normalizeOfferedFormats = raw => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const allowed = raw
    .map(value => (SESSION_FORMATS.includes(value) ? value : sessionFormatFromVariantName(value)))
    .filter(Boolean);
  return SESSION_FORMATS.filter(format => allowed.includes(format));
};

export const offeredFormatsFromPublicData = (publicData, { fallbackAll = true } = {}) => {
  const unique = normalizeOfferedFormats(
    publicData?.offeredFormats || publicData?.['offered-formats']
  );
  if (unique.length > 0) {
    return unique;
  }
  return fallbackAll ? [...SESSION_FORMATS] : [];
};

export const offeredFormatsFromListing = listing =>
  offeredFormatsFromPublicData(listing?.attributes?.publicData);

export const isOfferedFormatsListingField = fieldConfig =>
  fieldConfig?.key === OFFERED_FORMATS_FIELD;

export const isStartNowBooking = (orderData, transaction) =>
  orderData?.startNow === true || transaction?.attributes?.protectedData?.startNow === true;

const bookingMs = value => {
  if (value instanceof Date) {
    return value.getTime();
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/**
 * Start now window: next 15-minute slot whose end is still a full session ahead.
 * Reusing a stored slot (checkout localStorage is kept for a day) is what made
 * Sharetribe reject “Booking end date cannot be in the past.”
 *
 * Times are UTC-aligned 15-minute instants (same wall times in hour-offset
 * listing zones such as America/New_York).
 *
 * @param {Object} [params]
 * @param {Date} [params.now]
 * @param {string} [params.timeZone] unused; kept so callers can pass listing TZ
 * @param {number} [params.minutes] session length
 * @param {number} [params.leadMs] clock-skew / request-latency pad
 * @returns {{ bookingStart: Date, bookingEnd: Date, bookingStartTime: string, bookingEndTime: string }}
 */
export const instantBookingWindow = ({
  now = new Date(),
  timeZone: _timeZone,
  minutes = SESSION_LENGTH_MINUTES,
  leadMs = 20 * 1000,
} = {}) => {
  const lengthMs = minutes * 60 * 1000;
  const nowMs = now.getTime();
  const earliestStart = nowMs + leadMs;
  let bookingStartMs = Math.ceil(earliestStart / lengthMs) * lengthMs;
  let bookingEndMs = bookingStartMs + lengthMs;
  const minEndMs = nowMs + lengthMs + leadMs;
  while (bookingEndMs <= minEndMs) {
    bookingStartMs += lengthMs;
    bookingEndMs = bookingStartMs + lengthMs;
  }
  const bookingStart = new Date(bookingStartMs);
  const bookingEnd = new Date(bookingEndMs);
  return {
    bookingStart,
    bookingEnd,
    bookingStartTime: `${bookingStartMs}`,
    bookingEndTime: `${bookingEndMs}`,
  };
};

/**
 * True when stored Start now dates are still safe to send to Marketplace API.
 *
 * @param {Object} [orderData]
 * @param {Date|number} [now]
 * @param {number} [minRemainingMs]
 * @returns {boolean}
 */
export const isStartNowWindowUsable = (
  orderData,
  now = new Date(),
  minRemainingMs = 2 * 60 * 1000
) => {
  const endMs = bookingMs(orderData?.bookingDates?.bookingEnd || orderData?.bookingEndTime);
  const startMs = bookingMs(orderData?.bookingDates?.bookingStart || orderData?.bookingStartTime);
  const nowMs = now instanceof Date ? now.getTime() : now;
  return (
    Number.isFinite(endMs) &&
    Number.isFinite(startMs) &&
    endMs > startMs &&
    endMs > nowMs + minRemainingMs
  );
};

/**
 * Replace Start now booking dates with a live now → now+15m window.
 *
 * @param {Object} [orderData]
 * @param {Object} [params]
 * @param {Date} [params.now]
 * @param {string} [params.timeZone]
 * @returns {Object}
 */
export const refreshStartNowOrderData = (orderData = {}, { now, timeZone } = {}) => {
  if (orderData?.startNow !== true) {
    return orderData;
  }
  const window = instantBookingWindow({ now, timeZone });
  // Sweet Skillz: a skill Talk-now reserves its whole booked length (server re-validates).
  const skillMinutes = Number(orderData?.skillMinutes);
  if (orderData?.skillId && [30, 45, 60, 90].includes(skillMinutes)) {
    const startMs = new Date(window.bookingStart).getTime();
    window.bookingEnd = new Date(startMs + skillMinutes * 60000);
    window.bookingEndTime = String(startMs + skillMinutes * 60000);
  }
  return {
    ...orderData,
    bookingStartTime: window.bookingStartTime,
    bookingEndTime: window.bookingEndTime,
    bookingDates: {
      bookingStart: window.bookingStart,
      bookingEnd: window.bookingEnd,
    },
  };
};

/**
 * Next 15-minute Start now window after the one that just failed.
 *
 * @param {Object} [orderData]
 * @param {Object} [params]
 * @param {string} [params.timeZone]
 * @returns {Object}
 */
export const advanceStartNowOrderData = (orderData = {}, { timeZone } = {}) => {
  const startMs = bookingMs(orderData?.bookingDates?.bookingStart || orderData?.bookingStartTime);
  const now = Number.isFinite(startMs) ? new Date(startMs + 1000) : new Date();
  return refreshStartNowOrderData(orderData, { now, timeZone });
};

/**
 * OrderPanel values for the paid Start now path (no date picker).
 *
 * @param {Object} params
 * @param {string} params.sessionFormat
 * @param {string} [params.timeZone]
 * @param {Date} [params.now]
 * @returns {Object}
 */
export const buildInstantCheckoutValues = ({ sessionFormat, timeZone, now } = {}) => {
  const window = instantBookingWindow({ now, timeZone });
  return {
    sessionFormat,
    priceVariantName: variantNameForFormat(sessionFormat),
    bookingStartTime: window.bookingStartTime,
    bookingEndTime: window.bookingEndTime,
    startNow: true,
  };
};
